/**
 * Revenue Overview Page
 *
 * Combined Stripe + Apple + internal revenue: provider-split MRR/ARR,
 * cash collected, trend + movement, per-org breakdown, and risk.
 * Single source of truth — intended to absorb the Founder Dashboard
 * Revenue tab and the Stripe page over time.
 *
 * Design: deep-navy "ledger band" hero with a provider-split bar
 * (the page's thesis: one revenue picture, split by provider), quiet
 * white cards below. Brand tokens from tailwind.config.js.
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { founderDashboardAPI } from '../services/api';
import { useEnvironment } from '../context/EnvironmentContext';
import TrendChart from '../components/founder-dashboard/TrendChart';
import DataTable from '../components/founder-dashboard/DataTable';
import RefreshIndicator from '../components/founder-dashboard/RefreshIndicator';

const PROVIDER_META = {
  stripe: { label: 'Stripe', dot: 'bg-indigo-400', bar: 'bg-indigo-400', pill: 'bg-indigo-100 text-indigo-700' },
  apple: { label: 'Apple', dot: 'bg-cloud-white', bar: 'bg-cloud-white', pill: 'bg-deep-navy text-cloud-white' },
  internal: { label: 'Comped', dot: 'bg-amber-400', bar: 'bg-amber-400', pill: 'bg-amber-100 text-amber-700' },
};

// Currency with cents when they matter ($4.99 stays $4.99, $200 stays $200)
const fmtMoney = (value) => {
  const v = value || 0;
  const hasCents = Math.round(v * 100) % 100 !== 0;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: hasCents ? 2 : 0,
  }).format(v);
};

// Signed variant for movement deltas: +$50 / −$29
const fmtSigned = (value) => {
  const v = value || 0;
  if (v === 0) return fmtMoney(0);
  return `${v > 0 ? '+' : '−'}${fmtMoney(Math.abs(v))}`;
};

const fmtDate = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  return isNaN(d.getTime())
    ? '—'
    : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

function ProviderPill({ provider }) {
  const meta = PROVIDER_META[provider];
  return (
    <span
      className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${
        meta ? meta.pill : 'bg-gray-100 text-gray-600'
      }`}
    >
      {meta ? meta.label : provider}
    </span>
  );
}

// Small uppercase section label — quieter than icon headers
function Eyebrow({ children }) {
  return (
    <p className="text-xs uppercase tracking-widest text-storm-blue font-semibold mb-3">
      {children}
    </p>
  );
}

// Quiet stat tile that sits on the navy band
function BandTile({ label, value, hint, loading }) {
  return (
    <div className="rounded-xl bg-white/[0.07] border border-white/10 px-4 py-3">
      <p className="text-[11px] uppercase tracking-wider text-sky-blue font-semibold">{label}</p>
      {loading ? (
        <div className="h-6 w-20 bg-white/10 rounded animate-pulse mt-1.5 mb-0.5"></div>
      ) : (
        <p className="text-xl font-bold tabular-nums tracking-tight text-cloud-white mt-1">
          {value ?? '—'}
        </p>
      )}
      {hint && <p className="text-[11px] text-sky-blue/80 mt-0.5">{hint}</p>}
    </div>
  );
}

// Compact light stat card for the movement row
function StatCard({ title, value, valueClass = 'text-gray-900', subtitle, loading }) {
  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 animate-pulse">
        <div className="h-3 bg-gray-200 rounded w-1/2 mb-2"></div>
        <div className="h-6 bg-gray-200 rounded w-3/4 mb-1"></div>
        <div className="h-2 bg-gray-200 rounded w-1/3"></div>
      </div>
    );
  }
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
      <p className="text-xs text-gray-500 uppercase tracking-wide font-medium">{title}</p>
      <p className={`text-xl font-bold tabular-nums mt-1 ${valueClass}`}>{value ?? '—'}</p>
      {subtitle && <p className="text-xs text-gray-400 mt-1">{subtitle}</p>}
    </div>
  );
}

// The signature element: one horizontal bar splitting MRR by provider
function ProviderSplitBar({ byProvider, loading }) {
  const segments = ['stripe', 'apple', 'internal'].map((key) => ({
    key,
    ...PROVIDER_META[key],
    amount: byProvider?.[key] || 0,
  }));
  const total = segments.reduce((sum, s) => sum + s.amount, 0);

  if (loading) {
    return <div className="h-2.5 rounded-full bg-white/10 animate-pulse"></div>;
  }

  return (
    <div>
      <div className="flex h-2.5 rounded-full overflow-hidden bg-white/10">
        {total > 0 &&
          segments
            .filter((s) => s.amount > 0)
            .map((s) => (
              <div
                key={s.key}
                className={s.bar}
                style={{ width: `${(s.amount / total) * 100}%` }}
                title={`${s.label}: ${fmtMoney(s.amount)}`}
              />
            ))}
      </div>
      <div className="flex flex-wrap gap-x-5 gap-y-1 mt-2.5">
        {segments.map((s) => (
          <span key={s.key} className="inline-flex items-center gap-1.5 text-sm text-cloud-white/90">
            <span className={`w-2 h-2 rounded-full ${s.dot}`}></span>
            {s.label}
            <span className="tabular-nums font-semibold">{fmtMoney(s.amount)}</span>
            {s.key === 'internal' && (
              <span className="text-sky-blue text-xs">not in headline</span>
            )}
          </span>
        ))}
      </div>
    </div>
  );
}

export default function RevenueOverviewPage() {
  const navigate = useNavigate();
  const { currentEnv } = useEnvironment();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dataEnv, setDataEnv] = useState(null); // Track which env the data/error belongs to
  const [movementWindow, setMovementWindow] = useState('30');

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await founderDashboardAPI.getRevenueOverview();
      setData(response.data);
      setDataEnv(currentEnv);
    } catch (err) {
      console.error('[RevenueOverviewPage] Error fetching data:', err);
      setError(err.response?.data?.error || err.message || 'Failed to load data');
      setDataEnv(currentEnv);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Clear all state and refetch when environment changes
    setData(null);
    setError(null);
    fetchData();
    const interval = setInterval(fetchData, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [currentEnv]);

  // Only show error if it's from the current environment
  if (error && dataEnv === currentEnv) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
        <p className="text-red-800 font-medium">Error loading revenue data</p>
        <p className="text-red-600 text-sm mt-1">{error}</p>
        <button
          onClick={fetchData}
          className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  const kpis = data?.kpis || {};
  // Cumulative cash series: running totals, nulls zero-filled so stacking works
  const cumulativeCash = (data?.trend?.months || []).reduce((acc, m) => {
    const prev = acc[acc.length - 1] || { stripeCash: 0, appleNet: 0 };
    acc.push({
      date: m.month,
      stripeCash: prev.stripeCash + (m.stripeCash || 0),
      appleNet: prev.appleNet + (m.appleNetMrr || 0),
    });
    return acc;
  }, []);
  const byProvider = kpis.byProvider || {};
  const counts = kpis.counts || {};
  const cash = kpis.cashCollected || {};
  const movement = data?.movement?.[movementWindow] || {};
  const organizations = data?.organizations || [];
  const risk = data?.risk || {};
  const failedPayments = risk.failedPayments || {};
  const revenueAtRisk = risk.revenueAtRisk || {};
  const appleRisk = risk.apple || {};
  const limitations = data?.meta?.limitations || [];

  const orgColumns = [
    {
      key: 'organizationName',
      header: 'Organization',
      sortable: true,
      render: (val) => <span className="font-medium text-gray-900">{val}</span>,
    },
    {
      key: 'customerEmail',
      header: 'Customer Email',
      sortable: true,
      render: (val) =>
        val ? (
          <a
            href={`mailto:${val}`}
            onClick={(e) => e.stopPropagation()}
            className="text-storm-blue hover:underline"
          >
            {val}
          </a>
        ) : (
          <span className="text-gray-400">—</span>
        ),
    },
    {
      key: 'provider',
      header: 'Provider',
      sortable: true,
      render: (val) => <ProviderPill provider={val} />,
    },
    {
      key: 'tierCode',
      header: 'Tier',
      sortable: true,
      render: (val) => <span className="capitalize">{(val || '').replace('_', ' ')}</span>,
    },
    { key: 'billingPeriod', header: 'Billing', sortable: true, render: (val) => <span className="capitalize">{val || '—'}</span> },
    { key: 'mrr', header: 'MRR', sortable: true, render: (val) => <span className="tabular-nums font-semibold">{fmtMoney(val)}</span> },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      render: (val, row) => (
        <div className="flex items-center gap-2">
          <span
            className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${
              val === 'past_due' ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'
            }`}
          >
            {val}
          </span>
          {(row.cancelAtPeriodEnd || row.appleAutoRenewStatus === false) && (
            <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-semibold bg-orange-100 text-orange-700">
              cancelling
            </span>
          )}
        </div>
      ),
    },
    { key: 'renewsAt', header: 'Renews', sortable: true, render: (val) => fmtDate(val) },
  ];

  const movementCards = [
    { key: 'newBusiness', title: 'New Business' },
    { key: 'upgrades', title: 'Upgrades' },
    { key: 'downgrades', title: 'Downgrades' },
    { key: 'churn', title: 'Churn' },
  ];

  return (
    <div className="space-y-8 max-w-7xl">
      {/* Ledger band — headline MRR + provider split */}
      <div className="rounded-card bg-gradient-to-b from-deep-navy to-midnight-blue shadow-card p-6 md:p-8">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-widest text-sky-blue font-semibold">
              Monthly recurring revenue
            </p>
            {loading ? (
              <div className="h-12 w-48 bg-white/10 rounded animate-pulse mt-3 mb-1"></div>
            ) : (
              <p className="text-5xl font-bold tabular-nums tracking-tight text-cloud-white mt-2">
                {fmtMoney(kpis.mrr)}
              </p>
            )}
            <p className="text-sm text-sky-blue mt-1.5">
              Stripe + Apple, comped excluded · Apple at list price
            </p>
          </div>
          <div className="text-cloud-white [&_button]:text-sky-blue [&_span]:text-sky-blue">
            <RefreshIndicator lastUpdated={data?.timestamp} onRefresh={fetchData} loading={loading} />
          </div>
        </div>

        <div className="mt-6">
          <ProviderSplitBar byProvider={byProvider} loading={loading} />
        </div>

        <div className="mt-6 pt-6 border-t border-white/10 grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
          <BandTile label="ARR" value={fmtMoney(kpis.arr)} hint="MRR × 12" loading={loading} />
          <BandTile
            label="Apple Net"
            value={fmtMoney(kpis.appleEstimatedNetMrr)}
            hint="est. @ 85%"
            loading={loading}
          />
          <BandTile
            label="Cash · Month"
            value={fmtMoney(cash.thisMonth)}
            hint="Stripe, tax-incl."
            loading={loading}
          />
          <BandTile
            label="Cash · Last"
            value={fmtMoney(cash.lastMonth)}
            hint="Stripe, tax-incl."
            loading={loading}
          />
          <BandTile
            label="Cash · YTD"
            value={fmtMoney(cash.yearToDate)}
            hint={`since Jan 1, ${new Date().getFullYear()}`}
            loading={loading}
          />
          <BandTile
            label="Paying Orgs"
            value={counts.payingOrgs}
            hint={`${counts.activeTrials ?? 0} active trials`}
            loading={loading}
          />
        </div>
      </div>

      {/* Trend */}
      <section>
        <Eyebrow>Cash collected · cumulative since launch (Feb 2026)</Eyebrow>
        <div className="bg-white rounded-card shadow-sm border border-gray-100 p-6">
          {loading ? (
            <div className="h-64 animate-pulse bg-gray-100 rounded"></div>
          ) : (
            <>
              <TrendChart
                data={cumulativeCash}
                lines={[
                  { key: 'stripeCash', name: 'Stripe cash', color: '#415A77', stackId: 'cash' },
                  { key: 'appleNet', name: 'Apple est. net', color: '#778DA9', stackId: 'cash' },
                ]}
                type="area"
                xAxisKey="date"
                height={250}
                yTickFormatter={(v) => `$${Math.round(v).toLocaleString()}`}
              />
              <p className="text-xs text-gray-400 mt-2">
                Running total. Apple has no payment history — its layer only accrues from the
                current-month net estimate.
              </p>
            </>
          )}
        </div>
      </section>

      {/* Movement */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <Eyebrow>MRR movement</Eyebrow>
          <div className="flex rounded-lg border border-storm-blue/30 overflow-hidden text-sm">
            {['30', '90'].map((w) => (
              <button
                key={w}
                onClick={() => setMovementWindow(w)}
                className={`px-3 py-1.5 font-medium transition-colors ${
                  movementWindow === w
                    ? 'bg-deep-navy text-cloud-white'
                    : 'bg-white text-storm-blue hover:bg-cloud-white/60'
                }`}
              >
                {w} days
              </button>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {movementCards.map(({ key, title }) => {
            const bucket = movement[key] || { count: 0, mrrDelta: 0 };
            const valueClass =
              bucket.mrrDelta > 0
                ? 'text-emerald-600'
                : bucket.mrrDelta < 0
                  ? 'text-red-600'
                  : 'text-gray-900';
            return (
              <StatCard
                key={key}
                title={title}
                value={loading ? null : fmtSigned(bucket.mrrDelta)}
                valueClass={valueClass}
                subtitle={`${bucket.count} ${bucket.count === 1 ? 'change' : 'changes'}`}
                loading={loading}
              />
            );
          })}
        </div>
        {!loading && (
          <p className="text-xs text-gray-400 mt-2">
            From the tier-change ledger (approximate — see limitations). Plus{' '}
            {movement.canceledSubs?.count ?? 0} canceled subscription
            {(movement.canceledSubs?.count ?? 0) === 1 ? '' : 's'} (
            {fmtMoney(movement.canceledSubs?.mrrLost)} MRR) not captured in the ledger.
          </p>
        )}
      </section>

      {/* Per-org revenue */}
      <section>
        <Eyebrow>Paying organizations</Eyebrow>
        <DataTable
          data={organizations.map((o) => ({ ...o, id: o.organizationId }))}
          columns={orgColumns}
          loading={loading}
          defaultSortKey="mrr"
          emptyMessage="No paying organizations"
          onRowClick={(row) => navigate(`/founder-dashboard/organizations/${row.organizationId}`)}
        />
      </section>

      {/* Risk & health */}
      <section>
        <Eyebrow>Risk &amp; health</Eyebrow>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Stripe failed payments */}
          <div className="bg-white rounded-card shadow-sm border border-gray-100 p-6">
            <h4 className="font-medium text-gray-700 mb-1">Failed Payments (30d)</h4>
            {loading ? (
              <div className="h-32 animate-pulse bg-gray-100 rounded mt-3"></div>
            ) : (
              <>
                <p className={`text-3xl font-bold tabular-nums ${failedPayments.count30d > 0 ? 'text-red-600' : 'text-gray-900'}`}>
                  {failedPayments.count30d ?? 0}
                </p>
                <div className="mt-3 space-y-2">
                  {(failedPayments.recent || []).map((fp, i) => (
                    <div key={i} className="flex items-center justify-between text-sm border-b border-gray-100 pb-2 last:border-0">
                      <div className="min-w-0">
                        <p className="font-medium text-gray-900 truncate">{fp.organizationName || 'Unknown org'}</p>
                        <p className="text-xs text-gray-400">{fp.failureReason || 'unknown reason'} · {fmtDate(fp.eventTimestamp)}</p>
                      </div>
                      <span className="text-red-600 font-medium tabular-nums ml-2">{fmtMoney(fp.amount)}</span>
                    </div>
                  ))}
                  {(failedPayments.recent || []).length === 0 && (
                    <p className="text-sm text-gray-400">No failed payments</p>
                  )}
                </div>
              </>
            )}
          </div>

          {/* Revenue at risk */}
          <div className="bg-white rounded-card shadow-sm border border-gray-100 p-6">
            <h4 className="font-medium text-gray-700 mb-1">Revenue at Risk (Past Due)</h4>
            {loading ? (
              <div className="h-32 animate-pulse bg-gray-100 rounded mt-3"></div>
            ) : (
              <>
                <p className={`text-3xl font-bold tabular-nums ${revenueAtRisk.totalMonthly > 0 ? 'text-red-600' : 'text-gray-900'}`}>
                  {fmtMoney(revenueAtRisk.totalMonthly)}
                </p>
                <div className="mt-3 space-y-2">
                  {(revenueAtRisk.subscriptions || []).map((sub, i) => (
                    <div key={i} className="flex items-center justify-between text-sm border-b border-gray-100 pb-2 last:border-0">
                      <div className="min-w-0">
                        <p className="font-medium text-gray-900 truncate">{sub.organizationName}</p>
                        <p className="text-xs text-gray-400 capitalize">{sub.tierCode} · {sub.provider}</p>
                      </div>
                      <span className="text-red-600 font-medium tabular-nums ml-2">{fmtMoney(sub.mrr)}/mo</span>
                    </div>
                  ))}
                  {(revenueAtRisk.subscriptions || []).length === 0 && (
                    <p className="text-sm text-gray-400">Nothing past due</p>
                  )}
                </div>
              </>
            )}
          </div>

          {/* Apple risk */}
          <div className="bg-white rounded-card shadow-sm border border-gray-100 p-6">
            <h4 className="font-medium text-gray-700 mb-1">Apple Subscriptions</h4>
            {loading ? (
              <div className="h-32 animate-pulse bg-gray-100 rounded mt-3"></div>
            ) : (
              <div className="mt-2 space-y-4">
                {[
                  { label: 'Cancelling (auto-renew off)', items: appleRisk.cancelling || [], dateKey: 'expiresAt' },
                  { label: 'In grace period', items: appleRisk.inGracePeriod || [], dateKey: 'graceExpiresAt' },
                  { label: 'Expiring within 15 days', items: appleRisk.expiringSoon || [], dateKey: 'expiresAt' },
                ].map(({ label, items, dateKey }) => (
                  <div key={label}>
                    <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">{label}</p>
                    {items.length === 0 ? (
                      <p className="text-sm text-gray-400 mt-1">None</p>
                    ) : (
                      items.map((item, i) => (
                        <div key={i} className="flex items-center justify-between text-sm mt-1">
                          <span className="font-medium text-gray-900 truncate">{item.organizationName}</span>
                          <span className="text-gray-500 ml-2">{fmtDate(item[dateKey])}</span>
                        </div>
                      ))
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Limitations footnote */}
      {limitations.length > 0 && (
        <div className="border-t border-gray-200 pt-4">
          <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-2">Known limitations</p>
          <ul className="text-xs text-gray-400 space-y-1 list-disc list-inside">
            {limitations.map((l, i) => (
              <li key={i}>{l}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
